import React, { useEffect, useState } from "react";
import {
  ThemeProvider,
  StyledEngineProvider,
  useTheme
} from "@mui/material/styles";
import { Box, CssBaseline } from "@mui/material";
import withTheme from "@mui/styles/withTheme";
import LearningCenterTopNav from "./LearningCenterTopNav";
import learningCenterTheme from "../../theme/learningCenterTheme";
import {
  root,
  dashboard,
  coursePlayer,
  contentViewer,
  apps,
  settings,
  learn,
  librarySearch,
  performance,
  routePermissions,
  messages,
  announcements,
  calendar,
  resources as resourcesRoute,
  resourceViewer,
  profile,
  courseOverview,
  appPrefix as learningCenterApp,
  policyPlayerResourceViewer
} from "../../routes/LearningCenterRoutes";
import visionLogoImage from "../../assets/images/vision_logo.jpeg";
import { connect } from "react-redux";
import { compose } from "redux";
import { fetchUnreadMessages } from "../../redux/actions/messagesActions";
import {
  fetchSupportInfo,
  changeSupportModalOpenState
} from "../../redux/actions/supportActions";
import { fetchTodos } from "../../redux/actions/learningCenter/dashboard/todoActions";
import WelcomeSlideOut from "../../views/WelcomeSlideOut/WelcomeSlideOut";
import ThemeWrapper from "../../theme/ThemeWrapper";
import { fetchResources } from "../../redux/actions/learningCenter/resourcesActions";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import ModalContainer from "../../components/ModalContainer/ModalContainer";
import PolicyPartnerLoginFrame from "../../components/PolicyPartnerLoginFrame/PolicyPartnerLoginFrame";
import SupportModal from "../../components/SupportModal";
import { useTranslation, withTranslation } from "react-i18next";
import TermsAndConditions from "../../views/MiscPages/TermsAndConditions";
import LearningCenterSideNav from "./LearningCenterSideNav";
import LearningCenterDashboard from "./LearningCenterDashboard";
import { Route, Redirect } from "react-router-dom";
import LearningCenterLearn from "./LearningCenterLearn";
import LearningCenterSettings from "./LearningCenterSettings";
import LearningCenterPerformance from "./LearningCenterPerformance";
import LearningCenterMyApps from "./LearningCenterMyApps";
import LearningCenterLibrarySearch from "./LearningCenterLibrarySearch";
import CoursePlayerWrapper from "./CoursePlayer/CoursePlayerWrapper";
import ContentViewerContainer from "./ContentViewerContainer";
import LearningCenterMessages from "./LearningCenterMessages";
import LearningCenterAnnouncements from "./LearningCenterAnnouncements";
import LearningCenterCalendar from "./LearningCenterCalendar";
import ResourceViewerContainer from "../../components/ResourceViewer/index";
import LearningCenterProfile from "./LearningCenterProfile";
import CourseOverviewContainer from "../../components/ContentViewer/CourseOverview";
import { SnackbarProvider } from "notistack";
import SnackbarContainer from "../Snackbar/SnackbarContainer";
import FullPageLayout from "../../components/FullPageLayout/index";
import ResourcesView from "../Resources/Resources";
import { UserAssignmentProvider } from "../../context/UserAssignmentContext";
import RepositoryItemViewer from "./PolicyPlayer/RepositoryItemViewer";
import { makeStyles } from "@mui/styles";
import {
  useIsIframed,
  usePostMessageOnRouteChange,
  useParentRouteChangeListener
} from "../../hooks/utils/iframeUtils";

const useStyles = ({ isIframed }) =>
  makeStyles(theme => ({
    container: {
      position: "relative"
    },
    content: {
      display: "flex",
      width: `calc(100% - ${theme.sizes.sideNavBarWidthCondensed}px)`,
      [theme.breakpoints.down("sm")]: {
        width: "100%"
      },
      marginTop: theme.spacing(9),
      float: "right",
      ...(isIframed && { width: "100%", marginTop: 0, float: "unset" })
    },
    loadingSpinner: {
      top: "calc(50vh - 90px)",
      left: "calc(50vw - 40px)",
      position: "absolute"
    }
  }));

const LearningCenterRoutes = ({ userPermissions }) => (
  <>
    <Route exact path={root} render={() => <Redirect to={dashboard} />} />
    <Route path={dashboard} component={LearningCenterDashboard} />
    <Route path={apps} component={LearningCenterMyApps} />
    <Route path={learn} component={LearningCenterLearn} />
    <Route path={performance} component={LearningCenterPerformance} />
    {userPermissions[routePermissions.settings] && (
      <Route path={settings} component={LearningCenterSettings} />
    )}
    <Route path={librarySearch} component={LearningCenterLibrarySearch} />
    <Route path={calendar} component={LearningCenterCalendar} />
    <Route path={courseOverview} component={CourseOverviewContainer} />
    <Route path={coursePlayer} component={CoursePlayerWrapper} />
    <Route path={contentViewer} component={ContentViewerContainer} />
    <Route
      path={resourceViewer}
      render={props => (
        <ResourceViewerContainer LayoutComponent={FullPageLayout} {...props} />
      )}
    />
    <Route
      path={resourcesRoute}
      render={props => (
        <ResourcesView
          LayoutComponent={FullPageLayout}
          {...props}
          currentApp={learningCenterApp}
        />
      )}
    />
    <Route path={announcements} component={LearningCenterAnnouncements} />
    <Route path={messages} component={LearningCenterMessages} />
    <Route path={profile} component={LearningCenterProfile} />
    <Route path={policyPlayerResourceViewer} component={RepositoryItemViewer} />
  </>
);

const LearningCenter = ({
  fetchUnreadMessages,
  fetchResources,
  fetchTodos,
  i18n,
  error,
  userState,
  history,
  supportModalOpen,
  changeSupportModalOpenState,
  fetchSupportInfo,
  customerState,
  resources,
  loading,
  match,
  location: { pathname },
  logoImage,
  logoStyle,
  unreadMessages,
  learningCenterCaption
}) => {
  const [mobileNavbarOpen, setMobileNavbarOpen] = useState(false);
  const theme = useTheme();
  const isIframed = useIsIframed();
  const classes = useStyles({ isIframed })();
  const { t } = useTranslation();
  usePostMessageOnRouteChange();
  useParentRouteChangeListener();

  useEffect(() => {
    fetchUnreadMessages();
    fetchResources();
    fetchTodos();
  }, []);

  useEffect(() => {
    const { user } = userState;

    if (user && user.settings.preferred_language !== i18n.language) {
      i18n.changeLanguage(user.settings.preferred_language);
    }

    if (error) {
      if (error.status > 501 || (error.status >= 400 && error.status < 408)) {
        history.push("/vision/error");
      } else {
        // Handle other error cases
      }
    }
  }, [i18n.language, error, userState.user]);

  const handleSupportModalClose = () => {
    changeSupportModalOpenState(false);
  };

  const handleSupportClick = () => {
    fetchSupportInfo();
    changeSupportModalOpenState(true);
  };

  const user = userState.user;
  const currentCustomer = customerState.customer;
  const resourceSections = resources.resourceSections;
  const isLoading =
    userState.loading || resources.loading || customerState.loading;

  if (
    user.id &&
    currentCustomer.id &&
    !currentCustomer.termsAndConditions
      .map(tc => tc.id)
      .includes(user.tcAccepted)
  ) {
    return <TermsAndConditions />;
  }

  if ((!user.id || !currentCustomer.id || !resourceSections) && isLoading) {
    return (
      <div className={classes.loadingSpinner}>
        <LoadingSpinner visible={loading} />
      </div>
    );
  } else {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={learningCenterTheme(theme)}>
          <CssBaseline />
          <ThemeWrapper>
            <ModalContainer>
              <UserAssignmentProvider>
                <SnackbarProvider>
                  <WelcomeSlideOut
                    adminView={false}
                    currentUser={user}
                    fetchTodos={fetchTodos}
                    videoUrl={currentCustomer.welcomeVideoUrl}
                  />
                  <LearningCenterTopNav
                    handleMenuToggle={() =>
                      setMobileNavbarOpen(!mobileNavbarOpen)
                    }
                    logo={logoImage || visionLogoImage}
                    learningCenterCaption={
                      learningCenterCaption ||
                      t("learningCenterTopNav.allowedApps.Learning Center")
                    }
                    logoStyle={
                      logoStyle || t("learningCenterTopNav.logoAndText")
                    }
                    currentUser={user}
                    unreadMessages={unreadMessages}
                    currentPath={match.path}
                  />
                  <Box className={classes.container}>
                    <LearningCenterSideNav
                      logo={logoImage || visionLogoImage}
                      currentPath={pathname}
                      onMobileNavbarToggle={() =>
                        setMobileNavbarOpen(!mobileNavbarOpen)
                      }
                      onSupportClick={handleSupportClick}
                      mobileNavbarOpen={mobileNavbarOpen}
                      userPermissions={user.permissions}
                    />
                    <Box className={classes.content}>
                      <LearningCenterRoutes
                        userPermissions={user.permissions}
                      />
                    </Box>
                    <SupportModal
                      open={supportModalOpen}
                      onClose={handleSupportModalClose}
                    />
                  </Box>
                  <SnackbarContainer
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center"
                    }}
                  />
                </SnackbarProvider>
              </UserAssignmentProvider>
            </ModalContainer>
          </ThemeWrapper>
          <PolicyPartnerLoginFrame user={user} />
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
};

const mapStateToProps = state => ({
  logoImage: state.learningCenterSettingsState.logoImage,
  learningCenterCaption:
    state.learningCenterSettingsState.learningCenterCaption,
  logoStyle: state.learningCenterSettingsState.logoStyle,
  customColor: state.learningCenterSettingsState.customColor,
  secondaryColor: state.learningCenterSettingsState.secondaryColor,
  unreadMessages: state.messagesState.unreadMessages,
  userState: state.userState,
  customerState: state.customerState,
  resources: state.resourcesState,
  supportModalOpen: state.supportState.supportInfo.open,
  error: state.errorState.error
});

const mapDispatchToProps = {
  fetchUnreadMessages,
  fetchSupportInfo,
  fetchTodos,
  changeSupportModalOpenState,
  fetchResources
};

const TranslatedLearningCenter = withTranslation()(LearningCenter);

export default compose(
  withTheme,
  connect(mapStateToProps, mapDispatchToProps)
)(TranslatedLearningCenter);
